import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import moment from 'moment'
import classNames from 'classnames'
import { Badge, Button, IconButton, Popover, Theme, Tooltip, Typography } from '@material-ui/core'
import { createStyles, makeStyles } from '@material-ui/styles'
import NotificationsOutlinedIcon from '@material-ui/icons/NotificationsOutlined'
import {
  buildInboxRequest,
  buildMessageActionRequest,
  buildReadAllRequest,
  buildSeenRequest,
  countUnread,
  getCategoryLabel,
  InboxMessage,
  InboxResponse,
  INBOX_POLL_INTERVAL_MS,
  markMessagesRead,
  sanitizeNotificationHtml
} from './task-notifications-bell.helpers'
import { COLOR_BLUE, COLOR_WHITE } from '../../constants/style'
import { useIsImpersonating } from '../../hooks/useIsImpersonating'

const { REACT_APP_PORTAL_API_URL } = process.env

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    bellButton: {
      height: 50,
      width: 50,
      marginRight: theme.spacing(0.5)
    },
    bellIcon: {
      color: COLOR_WHITE,
      fontSize: 25
    },
    paper: {
      width: 380,
      maxHeight: 520,
      display: 'flex',
      flexDirection: 'column'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: theme.spacing(1.5, 2),
      borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
    },
    headerTitle: {
      fontWeight: 600,
      fontSize: 15
    },
    markAllButton: {
      textTransform: 'none',
      fontSize: 13
    },
    list: {
      overflowY: 'auto'
    },
    empty: {
      padding: theme.spacing(4, 2),
      textAlign: 'center',
      color: 'rgba(0, 0, 0, 0.54)',
      fontSize: 14
    },
    row: {
      position: 'relative',
      padding: theme.spacing(1.5, 2, 1.5, 3.5),
      borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
      cursor: 'default',
      '&:last-child': {
        borderBottom: 'none'
      }
    },
    rowUnread: {
      backgroundColor: '#F3F9FF',
      cursor: 'pointer'
    },
    unreadDot: {
      position: 'absolute',
      left: theme.spacing(1.25),
      top: theme.spacing(2.25),
      width: 8,
      height: 8,
      borderRadius: 4,
      backgroundColor: COLOR_BLUE
    },
    meta: {
      fontSize: 12,
      color: 'rgba(0, 0, 0, 0.54)'
    },
    title: {
      fontWeight: 600,
      fontSize: 14,
      lineHeight: 1.4,
      marginTop: 2
    },
    content: {
      fontSize: 13,
      color: 'rgba(0, 0, 0, 0.72)',
      marginTop: 2,
      wordBreak: 'break-word',
      '& p': {
        margin: 0
      }
    },
    cta: {
      marginTop: theme.spacing(1),
      textTransform: 'none'
    }
  })
)

function readToken (): string | null {
  try {
    return localStorage.getItem('token')
  } catch {
    return null
  }
}

function isNotFound (error: any): boolean {
  return error?.response?.status === 404
}

export const TaskNotificationsBell: FC = () => {
  const isImpersonating = useIsImpersonating()
  const classes = useStyles()
  const requestSequenceRef = useRef(0)
  const [messages, setMessages] = useState<InboxMessage[]>([])
  const [newCount, setNewCount] = useState(0)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)

  const load = useCallback(() => {
    const token = readToken()
    if (!REACT_APP_PORTAL_API_URL || !token) return

    const requestId = requestSequenceRef.current + 1
    requestSequenceRef.current = requestId
    const { url, ...config } = buildInboxRequest({ apiUrl: REACT_APP_PORTAL_API_URL, token })
    axios.get<InboxResponse>(url, config)
      .then(response => {
        if (requestSequenceRef.current !== requestId) return
        setMessages(response.data?.data ?? [])
        setNewCount(response.data?.newCount ?? 0)
      })
      .catch(error => {
        // Non-critical: keep the last known list and retry on the next tick.
        console.warn('[TaskNotificationsBell] inbox poll failed', error?.response?.status ?? error)
      })
  }, [])

  useEffect(() => {
    load()
    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible') load()
    }, INBOX_POLL_INTERVAL_MS)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') load()
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      window.clearInterval(interval)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [load])

  const dropMessage = useCallback((id: string) => {
    requestSequenceRef.current += 1
    setMessages(prev => prev.filter(message => message.id !== id))
    load()
  }, [load])

  const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
    // Invalidate any in-flight poll so a stale newCount cannot bring the badge back.
    requestSequenceRef.current += 1
    setNewCount(0)
    const token = readToken()
    // An impersonating admin's look must not clear the customer's badge.
    if (!REACT_APP_PORTAL_API_URL || !token || isImpersonating) return
    const { url, body, headers } = buildSeenRequest({ apiUrl: REACT_APP_PORTAL_API_URL, token })
    // Refetch only after seen settles, so the fresh list carries the cleared count.
    axios.post(url, body, { headers })
      .catch(() => {
        // Idempotent; the next open retries it.
      })
      .then(load)
  }, [load, isImpersonating])

  const handleClose = useCallback(() => setAnchorEl(null), [])

  const sendMessageAction = useCallback((id: string, action: 'read' | 'click') => {
    // Invalidate any in-flight poll so it cannot overwrite the optimistic readAt.
    requestSequenceRef.current += 1
    setMessages(prev => markMessagesRead(prev, [id], new Date().toISOString()))
    const token = readToken()
    if (!REACT_APP_PORTAL_API_URL || !token || isImpersonating) return
    const { url, headers } = buildMessageActionRequest({ apiUrl: REACT_APP_PORTAL_API_URL, token, id, action })
    axios.patch(url, {}, { headers }).catch(error => {
      if (isNotFound(error)) dropMessage(id)
    })
  }, [dropMessage, isImpersonating])

  const handleRowClick = useCallback((message: InboxMessage) => {
    if (message.readAt === null) sendMessageAction(message.id, 'read')
  }, [sendMessageAction])

  const handleCtaClick = useCallback((event: React.MouseEvent, message: InboxMessage) => {
    // Keep the row click from also firing a separate read request.
    event.stopPropagation()
    sendMessageAction(message.id, 'click')
  }, [sendMessageAction])

  const handleMarkAllRead = useCallback(() => {
    requestSequenceRef.current += 1
    setMessages(prev => markMessagesRead(prev, 'all', new Date().toISOString()))
    const token = readToken()
    if (!REACT_APP_PORTAL_API_URL || !token || isImpersonating) return
    const { url, body, headers } = buildReadAllRequest({ apiUrl: REACT_APP_PORTAL_API_URL, token })
    axios.post(url, body, { headers }).catch(() => load())
  }, [load, isImpersonating])

  if (!REACT_APP_PORTAL_API_URL) return null

  const unread = countUnread(messages)
  // Badge uses the surface-wide newCount: only TASK_COMPLETE messages reach the
  // CMS surface today. Switch to a client-side count if other categories appear.

  return (
    <>
      <Tooltip title='Notifications'>
        <IconButton
          aria-haspopup='true'
          aria-label={newCount > 0 ? `Notifications, ${newCount} new` : 'Notifications'}
          aria-owns={open ? 'task-notifications-popover' : undefined}
          className={classes.bellButton}
          onClick={handleOpen}
        >
          <Badge
            badgeContent={newCount}
            color='error'
            max={9}
          >
            <NotificationsOutlinedIcon className={classes.bellIcon} />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        classes={{ paper: classes.paper }}
        id='task-notifications-popover'
        onClose={handleClose}
        open={open}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <div className={classes.header}>
          <Typography className={classes.headerTitle}>Notifications</Typography>
          <Button
            className={classes.markAllButton}
            color='primary'
            disabled={unread === 0}
            onClick={handleMarkAllRead}
            size='small'
          >
            Mark all as read
          </Button>
        </div>
        <div className={classes.list}>
          {messages.length === 0 ? (
            <Typography className={classes.empty}>No notifications yet</Typography>
          ) : messages.map(message => {
            const isUnread = message.readAt === null
            return (
              <div
                className={classNames(classes.row, isUnread && classes.rowUnread)}
                key={message.id}
                onClick={() => handleRowClick(message)}
              >
                {isUnread && <span className={classes.unreadDot} />}
                <Typography className={classes.meta}>
                  {getCategoryLabel(message.category)} · {moment(message.publishedAt).fromNow()}
                </Typography>
                <Typography className={classes.title}>{message.title}</Typography>
                <div
                  className={classes.content}
                  dangerouslySetInnerHTML={{ __html: sanitizeNotificationHtml(message.htmlContent) }}
                />
                {message.ctaUrl ? (
                  <Button
                    className={classes.cta}
                    color='primary'
                    href={message.ctaUrl}
                    onClick={event => handleCtaClick(event, message)}
                    rel='noopener noreferrer'
                    size='small'
                    target='_blank'
                    variant='outlined'
                  >
                    {message.ctaLabel || 'View'}
                  </Button>
                ) : null}
              </div>
            )
          })}
        </div>
      </Popover>
    </>
  )
}
